import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Stack,
    Button,
    TextField,
    MenuItem,
    Alert,
    Divider,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import { ArrowBack, AssignmentReturn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useGetCustomerOrderById } from '../../hooks/customer/useGetCustomerOrderById';
import { useCreateReturn } from '../../hooks/customer/useCreateReturn';
import {
    RETURN_REASONS,
    RETURN_CONDITIONS,
} from '../../components/customer/returns/returnConstants';

const ReturnRequest = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { order, isLoading } = useGetCustomerOrderById(id);
    const createMutation = useCreateReturn();

    const orderItems = order?.items || [];

    // Per-item draft: { [order_item_id]: { selected, qty, reason, condition, notes } }
    const [items, setItems] = useState({});
    const [overallReason, setOverallReason] = useState('');
    const [customerNotes, setCustomerNotes] = useState('');
    const [touched, setTouched] = useState(false);
    const [serverErrors, setServerErrors] = useState({});

    useEffect(() => {
        const next = {};
        orderItems.forEach((it) => {
            next[it.id] = {
                selected: false,
                qty: 1,
                reason: '',
                condition: '',
                notes: '',
            };
        });
        setItems(next);
    }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const setItemField = (itemId, field, value) =>
        setItems((prev) => ({
            ...prev,
            [itemId]: { ...(prev[itemId] || {}), [field]: value },
        }));

    const selectedItems = useMemo(
        () =>
            Object.entries(items)
                .filter(([, v]) => v.selected && Number(v.qty) > 0)
                .map(([k, v]) => ({ order_item_id: Number(k), ...v })),
        [items]
    );

    const canSubmit = selectedItems.length > 0 && Boolean(overallReason);

    const handleSubmit = (e) => {
        e.preventDefault();
        setTouched(true);
        if (!canSubmit) return;

        const payload = {
            order_id: Number(id),
            reason: overallReason,
            customer_notes: customerNotes || undefined,
            items: selectedItems.map((it) => ({
                order_item_id: it.order_item_id,
                qty: Number(it.qty),
                reason: it.reason || undefined,
                condition: it.condition || undefined,
                condition_notes: it.notes || undefined,
            })),
        };

        createMutation.mutate(payload, {
            onSuccess: (response) => {
                const newId = response?.data?.data?.id;
                if (newId) navigate(`/account/returns/${newId}`);
                else navigate('/account/returns');
            },
            onError: (err) => {
                if (err?.response?.status === 422) {
                    setServerErrors(err.response?.data?.errors || {});
                }
            },
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!order) {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Alert severity="error">{t('returns.orderNotFound', 'Order not found')}</Alert>
            </Container>
        );
    }

    const cannotReturn = !['delivered', 'shipped'].includes(order.status);

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
            <Button
                variant="text"
                startIcon={<ArrowBack />}
                onClick={() => navigate(`/orders`)}
                sx={{ mb: 2 }}
            >
                {t('common.back', 'Back')}
            </Button>

            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <AssignmentReturn color="primary" />
                    <Box>
                        <Typography variant="h5" fontWeight={800}>
                            {t('returns.requestTitle', 'Request a return')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('returns.forOrder', 'For order')}{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                #{order.number || order.id}
                            </Box>
                        </Typography>
                    </Box>
                </Stack>

                {cannotReturn && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {t(
                            'returns.notReturnable',
                            'This order is not eligible for a return at the moment.'
                        )}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t('returns.itemsToReturn', 'Items to return')}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />

                    {orderItems.map((it) => {
                        const draft = items[it.id] || {};
                        const itemErr = serverErrors[`items.${it.id}`] || serverErrors[`items.${it.id}.qty`];
                        return (
                            <Paper
                                key={it.id}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    mb: 1.25,
                                    borderRadius: 2,
                                    bgcolor: draft.selected ? 'action.hover' : 'transparent',
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <Checkbox
                                        checked={Boolean(draft.selected)}
                                        onChange={(e) => setItemField(it.id, 'selected', e.target.checked)}
                                        sx={{ pt: 0.5 }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {it.product?.name?.[isArabic ? 'ar' : 'en']
                                                || it.product?.name?.en
                                                || it.product?.name
                                                || `#${it.id}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('cart.qty', 'Qty')}: {it.qty}
                                        </Typography>

                                        {draft.selected && (
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={1.5}
                                                sx={{ mt: 1.5 }}
                                            >
                                                <TextField
                                                    label={t('returns.qty', 'Qty')}
                                                    type="number"
                                                    size="small"
                                                    inputProps={{ min: 1, max: Number(it.qty) }}
                                                    value={draft.qty}
                                                    onChange={(e) => setItemField(it.id, 'qty', Math.max(1, Math.min(Number(it.qty), Number(e.target.value) || 1)))}
                                                    sx={{ width: 100 }}
                                                />
                                                <TextField
                                                    select
                                                    size="small"
                                                    label={t('returns.reason', 'Reason')}
                                                    value={draft.reason}
                                                    onChange={(e) => setItemField(it.id, 'reason', e.target.value)}
                                                    sx={{ minWidth: 180 }}
                                                >
                                                    <MenuItem value="">—</MenuItem>
                                                    {RETURN_REASONS.map((r) => (
                                                        <MenuItem key={r.value} value={r.value}>
                                                            {isArabic ? r.labelAr : r.labelEn}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    select
                                                    size="small"
                                                    label={t('returns.condition', 'Condition')}
                                                    value={draft.condition}
                                                    onChange={(e) => setItemField(it.id, 'condition', e.target.value)}
                                                    sx={{ minWidth: 160 }}
                                                >
                                                    <MenuItem value="">—</MenuItem>
                                                    {RETURN_CONDITIONS.map((c) => (
                                                        <MenuItem key={c.value} value={c.value}>
                                                            {isArabic ? c.labelAr : c.labelEn}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Stack>
                                        )}

                                        {draft.selected && (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                multiline
                                                minRows={1}
                                                maxRows={3}
                                                placeholder={t('returns.notesPlaceholder', 'Optional notes…')}
                                                value={draft.notes}
                                                onChange={(e) => setItemField(it.id, 'notes', e.target.value)}
                                                sx={{ mt: 1.5 }}
                                            />
                                        )}

                                        {itemErr && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                                                {Array.isArray(itemErr) ? itemErr[0] : itemErr}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Paper>
                        );
                    })}

                    {touched && selectedItems.length === 0 && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                            {t('returns.pickAtLeastOne', 'Pick at least one item to return.')}
                        </Typography>
                    )}

                    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3 }} gutterBottom>
                        {t('returns.overall', 'Overall')}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />

                    <Stack spacing={2}>
                        <TextField
                            select
                            required
                            label={t('returns.overallReason', 'Reason for return')}
                            value={overallReason}
                            onChange={(e) => setOverallReason(e.target.value)}
                            error={touched && !overallReason}
                            helperText={touched && !overallReason ? t('returns.reasonRequired', 'Reason is required') : ''}
                        >
                            {RETURN_REASONS.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {isArabic ? r.labelAr : r.labelEn}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            multiline
                            minRows={3}
                            label={t('returns.customerNotes', 'Notes (optional)')}
                            value={customerNotes}
                            onChange={(e) => setCustomerNotes(e.target.value)}
                            placeholder={isArabic
                                ? 'مثال: المنتج وصل تالف من الجانب الأيمن…'
                                : 'e.g. The product arrived damaged on the right side…'}
                        />
                    </Stack>

                    {Object.keys(serverErrors).length > 0 && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {t('returns.fixErrors', 'Please fix the errors above and resubmit.')}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
                        <Button
                            variant="text"
                            onClick={() => navigate('/orders')}
                            disabled={createMutation.isPending}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={createMutation.isPending || cannotReturn || !canSubmit}
                        >
                            {createMutation.isPending
                                ? t('common.working', 'Working...')
                                : t('returns.submit', 'Submit return')}
                        </Button>
                    </Stack>
                </Box>
            </Paper>
        </Container>
    );
};

export default ReturnRequest;
